import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { SiteHeader } from "@/components/site/SiteHeader";
import { SiteFooter } from "@/components/site/SiteFooter";
import { SITE } from "@/lib/site";

export const Route = createFileRoute("/embeds")({
  head: () => ({
    meta: [
      { title: "Free embeds for local SEO agencies — Map Observer" },
      {
        name: "description",
        content:
          "Drop live Map Pack volatility, last-algorithm-update, and GBP health-check widgets onto your agency site. Free, no signup, always-on do-follow attribution.",
      },
      { property: "og:title", content: "Free local-SEO embeds — Map Observer" },
      {
        property: "og:description",
        content: "Three free widgets your agency clients will actually use.",
      },
    ],
    links: [{ rel: "canonical", href: `${SITE.url}/embeds` }],
  }),
  component: EmbedsPage,
});

interface EmbedDef {
  id: "volatility" | "last-update" | "health-check";
  name: string;
  description: string;
  height: number;
  bestFor: string;
}

const EMBEDS: EmbedDef[] = [
  {
    id: "volatility",
    name: "Local SERP Weather",
    description: "Live volatility score for the Google Map Pack, refreshed weekly with a 12-week sparkline.",
    height: 140,
    bestFor: "Agency homepages, dashboards, status pages.",
  },
  {
    id: "last-update",
    name: "Last Local Algorithm Update",
    description: "A one-line badge showing days since the last confirmed local algorithm shift, with severity.",
    height: 100,
    bestFor: "Footers, sidebar widgets, status bars.",
  },
  {
    id: "health-check",
    name: "GBP Health Check",
    description:
      "Free form: visitors enter a business name + city and get instant Google visibility signals. Great lead-gen on agency landing pages.",
    height: 360,
    bestFor: "Lead-gen pages, audit landing pages, free-tools pages.",
  },
];

function EmbedsPage() {
  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main className="mx-auto max-w-5xl px-5 sm:px-8 py-12">
        <p className="eyebrow">Embeds</p>
        <h1 className="headline-display mt-2 text-4xl sm:text-5xl">Free widgets for your agency site</h1>
        <p className="mt-4 max-w-2xl text-foreground/80">
          Three small embeds powered by Map Observer's live data. Drop them on any page in two lines of HTML.
          Each one includes a single, unobtrusive <em>Powered by Map Observer</em> do-follow link — the cost of
          living widgets you'd otherwise have to build yourself.
        </p>

        <div className="mt-10 grid gap-8">
          {EMBEDS.map((e) => (
            <EmbedCard key={e.id} embed={e} />
          ))}
        </div>

        <section className="mt-16 border-t border-border pt-8 text-sm text-muted-foreground">
          <h2 className="headline-display text-xl text-foreground">Terms</h2>
          <ul className="mt-3 list-disc pl-5 space-y-1">
            <li>Free for any website, commercial use included.</li>
            <li>Attribution link must remain visible. We update it from time to time; the loader handles that automatically.</li>
            <li>We may rate-limit abusive callers. No PII is collected from embed visitors.</li>
            <li>Data is provided as-is. The Local Search Index is licensed CC-BY 4.0.</li>
          </ul>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}

function EmbedCard({ embed }: { embed: EmbedDef }) {
  const snippet = `<div data-mapobserver-embed="${embed.id}"></div>
<script async src="${SITE.url}/embed/v1.js"></script>`;
  const [copied, setCopied] = useState(false);
  async function copy() {
    try {
      await navigator.clipboard.writeText(snippet);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // ignore
    }
  }
  return (
    <article className="grid gap-6 border border-border bg-paper-soft p-5 md:p-6 md:grid-cols-[1fr_1fr]">
      <div>
        <p className="eyebrow">{embed.id}</p>
        <h2 className="headline-display mt-1 text-2xl">{embed.name}</h2>
        <p className="mt-2 text-sm text-foreground/80">{embed.description}</p>
        <p className="mt-3 text-xs uppercase tracking-wider text-muted-foreground">Best for</p>
        <p className="text-sm text-foreground/80">{embed.bestFor}</p>

        <div className="mt-4">
          <pre className="overflow-auto rounded-sm border border-border bg-background p-3 text-[11px] leading-relaxed font-mono">
{snippet}
          </pre>
          <button
            type="button"
            onClick={copy}
            className="mt-3 rounded-sm border border-foreground bg-foreground px-4 py-2 text-xs uppercase tracking-wider text-background hover:bg-accent hover:border-accent transition-colors"
          >
            {copied ? "Copied!" : "Copy embed code"}
          </button>
        </div>
      </div>
      <div className="border border-border bg-background p-3">
        <p className="mb-2 text-[11px] uppercase tracking-wider text-muted-foreground">Live preview</p>
        <iframe
          src={`/embed/${embed.id}?theme=light&ref=preview`}
          title={`${embed.name} preview`}
          loading="lazy"
          style={{ width: "100%", border: 0, minHeight: embed.height, background: "transparent" }}
        />
      </div>
    </article>
  );
}
